import { useApi } from '@src/app/context/Api';
import { isEmpty, isUndefined, uniq } from 'lodash';
import { useCallback, useMemo, useState } from 'react';

/**
 * Hook: useCommonCriteria
 *
 * Makes API requests to retrieve the assets required for the wizard experience.
 *
 * @returns {{isLoading: boolean, data: *}}
 */
export const useCommonCriteria = () => {
  const { queryClient, useApiQueries } = useApi('Criteria');

  const [theCriteria, setTheCriteria] = useState({});
  const [theQueries, setTheQueries] = useState([]);

  const queries = useApiQueries(theQueries);

  const isLoading = useMemo(() => {
    return (
      isEmpty(queries) ||
      Object.keys(queries).length !== theQueries.length ||
      !isEmpty(
        Object.keys(queries).filter((queryKey) => {
          const query = queries[queryKey];
          return query?.isLoading || query?.isFetching;
        })
      )
    );
  }, [queries, theQueries]);

  const fetchData = useCallback((criteria = {}) => {
    let ids = [];

    // Entrance Criteria
    if (!isUndefined(criteria?.entrance?.commonIds) && !isEmpty(criteria?.entrance?.commonIds)) {
      ids = [...ids, ...criteria?.entrance?.commonIds];
    }

    // Exit Criteria
    if (!isUndefined(criteria?.exit?.commonIds) && !isEmpty(criteria?.exit?.commonIds)) {
      ids = [...ids, ...criteria?.exit?.commonIds];
    }

    ids = uniq(ids);

    if (!isEmpty(ids)) {
      const requests = [];

      ids.forEach((id) => {
        requests.push({
          apiKey: 'Criteria',
          payload: { id },
          queryKey: `GetCommonCriteria-${id}`,
        });
      });

      if (requests !== theQueries) {
        setTheQueries(requests);
      }

      if (criteria !== theCriteria) {
        setTheCriteria(criteria);
      }
    } else {
      setTheCriteria({});
      setTheQueries([]);
      queryClient.removeQueries({
        predicate: (query) => {
          return query.queryKey?.[0].startsWith('GetCommonCriteria');
        },
      });
    }
  }, []);

  const namesList = useMemo(() => {
    const theCommonCriteria = [];
    if (!isEmpty(queries)) {
      Object.keys(queries).forEach((queryKey) => {
        theCommonCriteria.push(queries?.[queryKey]?.data?.name);
      });
    }
    return theCommonCriteria;
  }, [queries]);

  const criteriaList = useMemo(() => {
    const theCommonCriteria = [];
    if (!isEmpty(queries)) {
      Object.keys(queries).forEach((queryKey) => {
        const context = [];

        const entrance = theCriteria?.entrance?.commonIds?.find((id) => {
          return queries?.[queryKey]?.data?.id === id;
        });

        if (!isUndefined(entrance)) {
          context.push('entrance');
        }

        const exit = theCriteria?.exit?.commonIds?.find((id) => {
          return queries?.[queryKey]?.data?.id === id;
        });

        if (!isUndefined(exit)) {
          context.push('exit');
        }

        theCommonCriteria.push({ ...queries?.[queryKey]?.data, ...{ context } });
      });
    }
    return theCommonCriteria;
  }, [queries]);

  return useMemo(() => {
    return { criteriaList, data: queries, fetch: fetchData, isLoading, namesList };
  }, [isLoading, queries, namesList, criteriaList]);
};
